import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg, fmtNum, fmtMonat, letzteMonate } from "@/lib/api";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldAlert, ShieldCheck, AlertTriangle, Info, Loader2, Ban, Unlock,
  PlayCircle, Save, Plus, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";
import { SupplierPicker } from "@/components/app/SupplierPicker";

const GRUND_LABELS = {
  hemmstoff: "Hemmstoff-Befund",
  keimzahl: "Keimzahl-Grenzwert",
  zellzahl: "Zellzahl-Grenzwert",
  manuell: "Manuelle Sperre",
};

const TYP_LABELS = {
  hemmstoff: "Hemmstoff",
  keimzahl: "Keimzahl",
  zellzahl: "Zellzahl",
  ausreisser_keimzahl: "Ausreißer (Keimzahl)",
  ausreisser_zellzahl: "Ausreißer (Zellzahl)",
};

const SchwereBadge = ({ schwere }) => {
  if (schwere === "kritisch")
    return <Badge data-testid="severity-badge-kritisch" variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />kritisch</Badge>;
  if (schwere === "warnung")
    return <Badge data-testid="severity-badge-warnung" className="bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]"><AlertTriangle className="h-3 w-3 mr-1" />Warnung</Badge>;
  return <Badge data-testid="severity-badge-info" variant="secondary"><Info className="h-3 w-3 mr-1" />Info</Badge>;
};

const fmtZeit = (iso) => {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ---------------------------------------------------------------- Verstöße
const VerstoesseTab = ({ liefernummer }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monat, setMonat] = useState("alle");
  const [schwere, setSchwere] = useState("alle");

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (monat !== "alle") params.monat = monat;
    if (schwere !== "alle") params.schwere = schwere;
    if (liefernummer) params.liefernummer = liefernummer;
    api.get("/verstoesse", { params })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [monat, schwere, liefernummer]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Verstöße & Hinweise</CardTitle>
            <CardDescription>Grenzwertüberschreitungen, Hemmstoff-Befunde und dokumentierte Ausreißer</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={monat} onValueChange={setMonat}>
              <SelectTrigger data-testid="violations-month-filter" className="w-[160px]">
                <SelectValue placeholder="Monat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Monate</SelectItem>
                {letzteMonate(12).map((m) => (
                  <SelectItem key={m} value={m}>{fmtMonat(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={schwere} onValueChange={setSchwere}>
              <SelectTrigger data-testid="violations-severity-filter" className="w-[140px]">
                <SelectValue placeholder="Schwere" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Schweren</SelectItem>
                <SelectItem value="kritisch">Kritisch</SelectItem>
                <SelectItem value="warnung">Warnung</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <Table data-testid="violations-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-card">Monat</TableHead>
                  <TableHead>Liefernr.</TableHead>
                  <TableHead>Lieferant</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Schwere</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className="sticky left-0 bg-card font-medium whitespace-nowrap">{fmtMonat(v.monat)}</TableCell>
                    <TableCell className="font-num">{v.liefernummer}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{v.lieferant_name}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{TYP_LABELS[v.typ] || v.typ}</TableCell>
                    <TableCell><SchwereBadge schwere={v.schwere} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[420px]">{v.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState icon={ShieldCheck} title="Keine Verstöße" text="Für die gewählten Filter liegen keine Einträge vor." />
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- Sperren
const SperreAnlegenDialog = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState({ liefernummer: "", grund: "manuell", details: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ liefernummer: "", grund: "manuell", details: "" });
  }, [open]);

  const speichern = async () => {
    if (!form.liefernummer) {
      toast.error("Bitte wählen Sie einen Lieferanten aus.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/sperren", form);
      toast.success("Liefersperre angelegt.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sperre-create-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Liefersperre anlegen</DialogTitle>
          <DialogDescription>Manuelle Sperre eines Lieferanten mit dokumentiertem Grund.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Lieferant</Label>
            <SupplierPicker value={form.liefernummer}
              onChange={(v) => setForm((f) => ({ ...f, liefernummer: v }))}
              testId="sperre-create-supplier-picker" />
          </div>
          <div className="space-y-1.5">
            <Label>Grund</Label>
            <Select value={form.grund} onValueChange={(v) => setForm((f) => ({ ...f, grund: v }))}>
              <SelectTrigger data-testid="sperre-create-reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(GRUND_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sperre-details">Details / Begründung</Label>
            <Textarea
              id="sperre-details"
              data-testid="sperre-create-details-input"
              rows={3}
              placeholder="z. B. wiederholte Beanstandungen, Vor-Ort-Kontrolle ausstehend …"
              value={form.details}
              onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button data-testid="sperre-create-submit-button" onClick={speichern} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
            Sperre anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SperreAufhebenDialog = ({ sperre, onClose, onSaved }) => {
  const [kommentar, setKommentar] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setKommentar(""); }, [sperre]);

  const aufheben = async () => {
    if (kommentar.trim().length < 5) {
      toast.error("Bitte geben Sie eine Begründung (mind. 5 Zeichen) an.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/sperren/${sperre.id}/aufheben`, { kommentar: kommentar.trim() });
      toast.success("Sperre aufgehoben.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!sperre} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sperre-lift-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sperre aufheben</DialogTitle>
          <DialogDescription>
            {sperre && <>Liefernummer {sperre.liefernummer} · {sperre.lieferant_name} · {GRUND_LABELS[sperre.grund] || sperre.grund}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="aufheben-kommentar">Begründung (Pflichtfeld)</Label>
          <Textarea
            id="aufheben-kommentar"
            data-testid="sperre-lift-comment-input"
            rows={3}
            placeholder="z. B. Nachprobe negativ, Werte wieder unter Grenzwert …"
            value={kommentar}
            onChange={(e) => setKommentar(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Die Aufhebung wird mit Datum, Bearbeiter und Begründung dokumentiert (Übersteuerung der automatischen Sperre).
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button data-testid="sperre-lift-submit-button" onClick={aufheben} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlock className="h-4 w-4 mr-2" />}
            Sperre aufheben
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SperrenTab = ({ liefernummer }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(liefernummer ? "alle" : "aktiv");
  const [anlegen, setAnlegen] = useState(false);
  const [aufheben, setAufheben] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (status !== "alle") params.status = status;
    if (liefernummer) params.liefernummer = liefernummer;
    api.get("/sperren", { params })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [status, liefernummer]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Lieferantensperren</CardTitle>
            <CardDescription>Automatische und manuelle Sperren – Aufhebung nur mit Begründung</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="sperren-status-filter" className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aktiv">Aktiv</SelectItem>
                <SelectItem value="aufgehoben">Aufgehoben</SelectItem>
                <SelectItem value="alle">Alle</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="sperre-create-button" size="sm" onClick={() => setAnlegen(true)}>
              <Plus className="h-4 w-4 mr-1" /> Sperre anlegen
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <Table data-testid="sperren-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-card">Liefernr.</TableHead>
                  <TableHead>Lieferant</TableHead>
                  <TableHead>Grund</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Angelegt</TableHead>
                  <TableHead>Aufhebung</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="sticky left-0 bg-card font-num font-medium">{s.liefernummer}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{s.lieferant_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={s.status === "aktiv" ? "destructive" : "secondary"} className="whitespace-nowrap">
                          {GRUND_LABELS[s.grund] || s.grund}
                        </Badge>
                        {s.automatisch && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">auto</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 max-w-[360px]">{s.details}</p>
                    </TableCell>
                    <TableCell>
                      {s.status === "aktiv" ? (
                        <Badge variant="destructive">aktiv</Badge>
                      ) : (
                        <Badge variant="secondary">aufgehoben</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {fmtZeit(s.erstellt_am)}<br />{s.erstellt_von}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[280px]">
                      {s.status === "aufgehoben" ? (
                        <>
                          {fmtZeit(s.aufgehoben_am)} · {s.aufgehoben_von}
                          <p className="italic mt-0.5">„{s.aufgehoben_kommentar}“</p>
                        </>
                      ) : "–"}
                    </TableCell>
                    <TableCell>
                      {s.status === "aktiv" && (
                        <Button
                          data-testid={`sperre-lift-button-${s.liefernummer}-${s.grund}`}
                          variant="outline" size="sm"
                          onClick={() => setAufheben(s)}
                        >
                          <Unlock className="h-4 w-4 mr-1" /> Aufheben
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState icon={ShieldCheck} title="Keine Sperren" text="Für den gewählten Status liegen keine Sperren vor." />
        )}
      </CardContent>
      <SperreAnlegenDialog open={anlegen} onClose={() => setAnlegen(false)} onSaved={load} />
      <SperreAufhebenDialog sperre={aufheben} onClose={() => setAufheben(null)} onSaved={load} />
    </Card>
  );
};

// ---------------------------------------------------------------- Regeln (Compliance-Konfiguration)
const RegelnTab = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pruefMonat, setPruefMonat] = useState(letzteMonate(2)[1]);
  const [pruefen, setPruefen] = useState(false);

  useEffect(() => {
    api.get("/compliance/konfiguration").then((res) => setForm(res.data)).catch(() => {});
  }, []);

  const speichern = async () => {
    setSaving(true);
    try {
      const res = await api.put("/compliance/konfiguration", {
        ...form,
        ausreisser_faktor: parseFloat(form.ausreisser_faktor) || 3.0,
        ausreisser_max_je_periode: parseInt(form.ausreisser_max_je_periode) || 1,
        grenzwert_monate_bis_sperre: parseInt(form.grenzwert_monate_bis_sperre) || 3,
      });
      setForm(res.data);
      toast.success("Regeln gespeichert.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const pruefungStarten = async () => {
    setPruefen(true);
    try {
      const res = await api.post("/compliance/pruefen", { monat: pruefMonat });
      toast.success(
        `Prüfung ${fmtMonat(res.data.monat)}: ${res.data.neue_verstoesse} neue Verstöße, ${res.data.anzahl_neue_sperren} neue Sperre(n).`
      );
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPruefen(false);
    }
  };

  if (!form)
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Ausreißer-Regelung</CardTitle>
          <CardDescription>
            Einzelne Extremwerte (z. B. akute Mastitis) können gemäß den güterechtlichen Bestimmungen
            bei der Berechnung des geometrischen Mittels unberücksichtigt bleiben.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Ausreißer-Regelung aktiv</p>
              <p className="text-xs text-muted-foreground">Extremwerte automatisch erkennen und ausschließen</p>
            </div>
            <Switch
              data-testid="compliance-outlier-active-switch"
              checked={form.ausreisser_aktiv}
              onCheckedChange={(v) => setForm((f) => ({ ...f, ausreisser_aktiv: v }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ausreisser-faktor">Ausreißer-Faktor</Label>
              <Input
                id="ausreisser-faktor" data-testid="compliance-outlier-factor-input"
                type="number" step="0.5" min="1.5"
                value={form.ausreisser_faktor}
                onChange={(e) => setForm((f) => ({ ...f, ausreisser_faktor: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Wert &gt; Faktor × geom. Mittel der übrigen Werte</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ausreisser-max">Max. je Zeitraum</Label>
              <Input
                id="ausreisser-max" data-testid="compliance-outlier-max-input"
                type="number" min="1" max="3"
                value={form.ausreisser_max_je_periode}
                onChange={(e) => setForm((f) => ({ ...f, ausreisser_max_je_periode: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Ausgeschlossene Werte je Berechnungszeitraum</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sperr-Verhalten</CardTitle>
          <CardDescription>
            Automatische Liefersperren gem. VO (EG) Nr. 853/2004 – jederzeit durch die Molkerei
            mit Begründung übersteuerbar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Automatische Sperren</p>
              <p className="text-xs text-muted-foreground">Bei Deaktivierung werden nur Verstöße protokolliert</p>
            </div>
            <Switch
              data-testid="compliance-autoblock-switch"
              checked={form.sperren_automatisch}
              onCheckedChange={(v) => setForm((f) => ({ ...f, sperren_automatisch: v }))}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Sofort-Sperre bei Hemmstoff</p>
              <p className="text-xs text-muted-foreground">Positiver Befund führt unmittelbar zur Liefersperre</p>
            </div>
            <Switch
              data-testid="compliance-inhibitor-block-switch"
              checked={form.hemmstoff_sperre_automatisch}
              onCheckedChange={(v) => setForm((f) => ({ ...f, hemmstoff_sperre_automatisch: v }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="monate-bis-sperre">Monate über Grenzwert bis zur Sperre</Label>
            <Input
              id="monate-bis-sperre" data-testid="compliance-months-input"
              type="number" min="1" max="12"
              value={form.grenzwert_monate_bis_sperre}
              onChange={(e) => setForm((f) => ({ ...f, grenzwert_monate_bis_sperre: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">Gesetzlicher Standard: 3 Monate in Folge (Keimzahl/Zellzahl)</p>
          </div>
          <Button data-testid="compliance-save-button" onClick={speichern} disabled={saving} className="w-full">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Regeln speichern
          </Button>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Prüfung manuell ausführen</CardTitle>
          <CardDescription>
            Führt die gesetzliche Prüfung (Ausreißer, Verstöße, Sperren) für einen Monat erneut aus –
            geschieht automatisch bei jedem Abrechnungslauf.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Select value={pruefMonat} onValueChange={setPruefMonat}>
            <SelectTrigger data-testid="compliance-check-month-select" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {letzteMonate(12).map((m) => (
                <SelectItem key={m} value={m}>{fmtMonat(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="compliance-check-run-button" onClick={pruefungStarten} disabled={pruefen}>
            {pruefen ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
            Prüfung starten
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------- Page
export default function AdminQualitaetPage() {
  const [kpi, setKpi] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "verstoesse";
  const initialLief = searchParams.get("liefernummer") || "";
  const [tab, setTab] = useState(initialTab);
  const [liefernummerFilter, setLiefernummerFilter] = useState(initialLief);

  // URL bei Tab-/Filterwechsel aktualisieren (Deep-Linking vom Dashboard)
  useEffect(() => {
    const params = new URLSearchParams();
    if (tab !== "verstoesse") params.set("tab", tab);
    if (liefernummerFilter) params.set("liefernummer", liefernummerFilter);
    setSearchParams(params, { replace: true });
  }, [tab, liefernummerFilter, setSearchParams]);

  useEffect(() => {
    Promise.all([
      api.get("/sperren", { params: { status: "aktiv" } }),
      api.get("/verstoesse"),
    ]).then(([sp, vs]) => {
      setKpi({
        sperren: sp.data.length,
        kritisch: vs.data.filter((v) => v.schwere === "kritisch").length,
        warnung: vs.data.filter((v) => v.schwere === "warnung").length,
        ausreisser: vs.data.filter((v) => v.typ?.startsWith("ausreisser")).length,
      });
    }).catch(() => {});
  }, []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">Qualität &amp; Sperren</h1>
        <p className="text-sm text-muted-foreground">
          Gesetzliche Bestimmungen: Ausreißer-Regelung, Grenzwert-Verstöße und Lieferantensperren
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card data-testid="quality-kpi-sperren">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10">
              <Ban className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Aktive Sperren</p>
              <p className="text-xl font-semibold font-num">{kpi ? fmtNum(kpi.sperren) : "–"}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="quality-kpi-kritisch">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10">
              <ShieldAlert className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Kritische Verstöße</p>
              <p className="text-xl font-semibold font-num">{kpi ? fmtNum(kpi.kritisch) : "–"}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="quality-kpi-warnung">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--warning))]/15">
              <AlertTriangle className="h-5 w-5 text-[hsl(var(--warning))]" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Warnungen</p>
              <p className="text-xl font-semibold font-num">{kpi ? fmtNum(kpi.warnung) : "–"}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="quality-kpi-ausreisser">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <FlaskConical className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Ausreißer dokumentiert</p>
              <p className="text-xl font-semibold font-num">{kpi ? fmtNum(kpi.ausreisser) : "–"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="quality-tabs" className="w-full sm:w-auto h-auto p-1">
          <TabsTrigger data-testid="quality-tab-verstoesse" value="verstoesse" className="flex-1 sm:flex-none">Verstöße</TabsTrigger>
          <TabsTrigger data-testid="quality-tab-sperren" value="sperren" className="flex-1 sm:flex-none">Sperren</TabsTrigger>
          <TabsTrigger data-testid="quality-tab-regeln" value="regeln" className="flex-1 sm:flex-none">Regeln</TabsTrigger>
        </TabsList>
        {liefernummerFilter && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm" data-testid="quality-filter-hint">
            <span className="text-muted-foreground">Filter aktiv:</span>
            <Badge variant="secondary" className="font-num">Liefernr. {liefernummerFilter}</Badge>
            <Button
              variant="ghost" size="sm"
              onClick={() => setLiefernummerFilter("")}
              data-testid="quality-filter-clear"
              className="ml-auto h-7"
            >
              Filter entfernen
            </Button>
          </div>
        )}
        <TabsContent value="verstoesse" className="mt-4"><VerstoesseTab liefernummer={liefernummerFilter} /></TabsContent>
        <TabsContent value="sperren" className="mt-4"><SperrenTab liefernummer={liefernummerFilter} /></TabsContent>
        <TabsContent value="regeln" className="mt-4"><RegelnTab /></TabsContent>
      </Tabs>
    </div>
  );
}
